import React, { useState, useCallback, useRef } from 'react';
import { M365User } from '../../types';
import { searchUsers } from '../../services/graphService';
import { useGraphClient } from '../../hooks/useGraphClient';
import Spinner from '../Shared/Spinner';

interface UserSearchProps {
  onSelectUser: (user: M365User) => void;
  label?: string;
  placeholder?: string;
  /**
   * Rend la liste de résultats dans le flux plutôt qu'en surimpression.
   * Indispensable dans une modale : son corps est en overflow-y-auto, un menu
   * positionné en absolu s'y ferait couper au bas du cadre.
   */
  inline?: boolean;
  /** Écarte un utilisateur des résultats — typiquement la personne offboardée */
  excludeUserId?: string;
  /**
   * Autorise l'usage d'une adresse tapée telle quelle si elle ne correspond à
   * aucun compte. Utile pour un transfert vers une adresse hors du tenant.
   */
  onUseRawAddress?: (address: string) => void;
}

/**
 * Champ de recherche d'utilisateur avec autocomplete
 * Recherche via Graph API après 2 caractères tapés
 */
const UserSearch: React.FC<UserSearchProps> = ({
  onSelectUser,
  label = 'Rechercher un utilisateur',
  placeholder = 'Nom, email ou UPN...',
  inline = false,
  excludeUserId,
  onUseRawAddress,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<M365User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const { getClient } = useGraphClient();
  const debounceRef = useRef<NodeJS.Timeout>();

  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const client = await getClient();
        const users = await searchUsers(client, searchQuery);
        setResults(excludeUserId ? users.filter((u) => u.id !== excludeUserId) : users);
        setShowResults(true);
      } catch (error) {
        console.error('Erreur recherche:', error);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [getClient, excludeUserId]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce de 300ms pour éviter trop de requêtes
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(value), 300);
  };

  const handleSelect = (user: M365User) => {
    setQuery(user.displayName);
    setShowResults(false);
    onSelectUser(user);
  };

  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.trim());

  // En surimpression, la liste flotte au-dessus du contenu ; en flux, elle pousse
  // ce qui suit, ce qui évite tout risque de découpe dans un conteneur qui scrolle.
  const listPosition = inline
    ? 'w-full mt-1 bg-white border border-ms-gray-200 rounded-md shadow-sm max-h-60 overflow-y-auto'
    : 'absolute z-10 w-full mt-1 bg-white border border-ms-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto';

  return (
    <div className={inline ? '' : 'relative'}>
      {label && (
        <label className="block text-sm font-medium text-ms-gray-700 mb-2">{label}</label>
      )}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowResults(true)}
          // En flux, on garde la liste ouverte : le blur au clic sur un résultat
          // la fermerait avant que le clic n'aboutisse.
          onBlur={inline ? undefined : () => setTimeout(() => setShowResults(false), 200)}
          placeholder={placeholder}
          className="w-full px-4 py-2.5 pl-10 border border-ms-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ms-blue/30 focus:border-ms-blue text-sm"
        />
        {/* Icône de recherche */}
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ms-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clipRule="evenodd"
          />
        </svg>
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner size="sm" />
          </div>
        )}
      </div>

      {/* Résultats */}
      {showResults && results.length > 0 && (
        <div className={listPosition}>
          {results.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => handleSelect(user)}
              className="w-full text-left px-4 py-3 hover:bg-ms-gray-50 border-b border-ms-gray-100 last:border-0 transition-colors"
            >
              <p className="text-sm font-medium text-ms-gray-800">
                {user.displayName}
                {!user.accountEnabled && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">
                    Désactivé
                  </span>
                )}
              </p>
              <p className="text-xs text-ms-gray-500">{user.mail || user.userPrincipalName}</p>
              {user.department && (
                <p className="text-xs text-ms-gray-400">{user.department}</p>
              )}
            </button>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && query.length >= 2 && !isSearching && (
        <div
          className={
            inline
              ? 'w-full mt-1 bg-white border border-ms-gray-200 rounded-md p-4'
              : 'absolute z-10 w-full mt-1 bg-white border border-ms-gray-200 rounded-md shadow-lg p-4'
          }
        >
          <p className="text-sm text-ms-gray-500 text-center">Aucun utilisateur trouvé</p>
          {/* Sortie de secours : une adresse hors du tenant n'apparaîtra jamais ici */}
          {onUseRawAddress && looksLikeEmail && (
            <button
              type="button"
              onClick={() => {
                onUseRawAddress(query.trim());
                setShowResults(false);
              }}
              className="mt-3 w-full px-3 py-2 text-sm text-ms-blue border border-ms-blue/30 rounded-md hover:bg-ms-blue-light transition-colors"
            >
              Utiliser « {query.trim()} » comme adresse externe
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearch;
